import { deleteCache, getOrSetCacheWithHit } from "@sajilni/cache";
import { prisma, type Prisma } from "client";
import { isMongoObjectId } from "@sajilni/utils/ticketQr";

const LANGUAGE_CACHE_KEY = "languages:all:v1";
const LANGUAGE_CACHE_TTL_SECONDS = 60 * 60;

const invalidateLanguageCache = async () => {
  try {
    await deleteCache(LANGUAGE_CACHE_KEY);
  } catch (error) {
    console.warn("[cache] language invalidation failed (non-blocking)", error);
  }
};

export const getLanguages = async () => {
  const { data: languages } = await getOrSetCacheWithHit(
    LANGUAGE_CACHE_KEY,
    async () => {
      const rawLanguages = await prisma.language.findMany();
      // Sort the languages array to ensure English appears first
      return [...rawLanguages].sort((a, b) => {
        if (a.code === "en") return -1;
        if (b.code === "en") return 1;
        return a.code.localeCompare(b.code);
      });
    },
    { ttlSeconds: LANGUAGE_CACHE_TTL_SECONDS }
  );
  return languages;
};

export const getLanguageById = async (id: string) => {
  const language = await prisma.language.findFirst({
    where: {
      id: id,
    },
  });
  return language;
};

export const createLanguage = async (
  languageBody: Prisma.LanguageCreateInput
) => {
  const language = await prisma.language.create({
    data: {
      ...languageBody,
    },
  });
  await invalidateLanguageCache();
  return language;
};

export const updateLanguage = async (
  id: string,
  languageBody: Prisma.LanguageUpdateInput
) => {
  const language = await prisma.language.update({
    where: {
      id: id,
    },
    data: {
      ...languageBody,
    },
  });
  await invalidateLanguageCache();
  return language;
};

export const deleteLanguage = async (id: string) => {
  const language = await prisma.language.delete({
    where: {
      id: id,
    },
  });
  await invalidateLanguageCache();
  return language;
};

/** Pick Event PK vs unique slug — never OR both (cuid in @db.ObjectId → P2023). */
export const eventIdOrSlugWhere = (eventIdOrSlug: string) =>
  isMongoObjectId(eventIdOrSlug)
    ? { id: eventIdOrSlug }
    : { slug: eventIdOrSlug };

export const getEventLanguagesByEventIdOrEventSlug = async (
  eventIdOrSlug: string
) => {
  // findUnique on id (PK) or slug (@unique) — no joins, no OR ObjectId trap
  const event = await prisma.event.findUnique({
    where: eventIdOrSlugWhere(eventIdOrSlug),
    select: { languagesIds: true },
  });
  if (!event?.languagesIds?.length) return [];

  return prisma.language.findMany({
    where: {
      id: {
        in: event.languagesIds,
      },
    },
  });
};
